import { getRandomItemFromArray } from 'src/replicant/utils/random';
import type { BlockType } from '../../minigame/match2-timestep/game/defs/block';
import type { BlockEntity } from '../../minigame/match2-timestep/game/entities/BlockEntity';
import type { GameScene } from '../../minigame/match2-timestep/game/GameScene';
import {
  blockIsSpecial,
  blockIterateAll,
} from '../../minigame/match2-timestep/game/util/blockTools';
import { getCollisionTracker } from '../../minigame/match2-timestep/game/util/CollisionTracker';
import { waitAFrame } from '../../minigame/match2-timestep/lib/util/waitAFrame';
import { runRumbleHeroPowerAnimation } from './dreamDrift.power.rumble.anim';

/** Eligible Rumble targets grouped by their shared obstacle type. */
type RumbleObstacleGroups = Map<BlockType, BlockEntity[]>;

/**
 * Check whether a board entity can be considered by Rumble Hero.
 *
 * @param entity Board entity being inspected.
 * @returns Whether the entity is a non-immortal obstacle.
 */
function isRumbleObstacle(entity: BlockEntity): boolean {
  // Basic tiles and power blocks are board pieces rather than obstacles.
  if (!blockIsSpecial(entity.c.block.blockId)) return false;

  // Immortal obstacles cannot lose a layer from a normal power hit.
  if (entity.c.block.props.immortal) return false;

  // The remaining special entity is eligible for Rumble targeting.
  return true;
}

/**
 * Collect every unique eligible obstacle on the current board by type.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @returns Unique eligible obstacle entities grouped by block type.
 */
function collectRumbleObstacleGroups(game: GameScene): RumbleObstacleGroups {
  const groups: RumbleObstacleGroups = new Map();
  const visited = new Set<BlockEntity>();

  // Inspect both the base and overlay obstacle occupying each board cell.
  blockIterateAll(game, (base, overlay) => {
    for (const entity of [base, overlay]) {
      // Empty overlay layers do not provide a target.
      if (!entity) continue;

      // Multi-cell obstacles should only appear once in the target snapshot.
      if (visited.has(entity)) continue;
      visited.add(entity);

      // Skip board entities outside Rumble's obstacle rules.
      if (!isRumbleObstacle(entity)) continue;

      // Group variants that share the same obstacle mechanic together.
      const obstacleType = entity.c.block.props.type;
      const existingGroup = groups.get(obstacleType);
      if (existingGroup) {
        existingGroup.push(entity);
        continue;
      }

      // Start the first target group for this obstacle type.
      groups.set(obstacleType, [entity]);
    }
  });

  // Return the complete board snapshot for random type selection.
  return groups;
}

/**
 * Remove one layer from up to eight obstacles of one random type on the board.
 *
 * @param game Active Dream Drift Match-2 runtime.
 */
export async function runRumbleHeroPower(game: GameScene): Promise<void> {
  // Snapshot every eligible obstacle before any hit changes the board.
  const obstacleGroups = collectRumbleObstacleGroups(game);

  // Retain an empty impact target list when this board has no obstacles.
  let targets: BlockEntity[] = [];

  // Choose one of the obstacle types currently represented on the board.
  if (obstacleGroups.size > 0) {
    const obstacleType = getRandomItemFromArray(
      [...obstacleGroups.keys()],
      Math.random,
    );
    // Cap direct hits while preserving the selected group's board order.
    targets = obstacleGroups.get(obstacleType)!.slice(0, 8);
  }
  const collisionTracker = getCollisionTracker();
  let impactApplied = false;

  /** Apply the selected obstacle hits at most once for this level opening. */
  const applyImpact = (): void => {
    // Ignore repeated presentation callbacks after the opening impact resolves.
    if (impactApplied) return;
    impactApplied = true;

    // Apply one normal power hit to each selected obstacle.
    for (const target of targets) {
      collisionTracker.collide(target.c.block, 'attack', { type: 'booster' });
    }
  };

  // Play the complete hero presentation and synchronize its authored impact.
  await runRumbleHeroPowerAnimation(game, applyImpact);

  // Presentations without a screenshake impact have no board activity to settle.
  if (targets.length === 0) return;

  // Give the collision system time to start resolving the scheduled hits.
  await waitAFrame();

  // Finish immediately if the hits produced no further board activity.
  if (game.phaseSystem.phase === 'input') return;

  // Keep Dream Drift input blocked until resulting falls and shuffles settle.
  await game.phaseSystem.waitForPhase('input');
}
